import LogRocket from 'logrocket';
import config from '../config';

/**
 * LogRocket Service - Singleton Pattern
 *
 * Manages LogRocket session recording, error tracking, and user identification
 * across the entire application. Provides privacy-focused, production-ready
 * implementation with graceful degradation.
 *
 * Features:
 * - Session recording with screen capture
 * - Automatic error capture
 * - User identification on authentication
 * - Custom event tracking
 * - Privacy-focused request/response sanitization
 * - Environment-based configuration
 */
class LogRocketService {
  private static instance: LogRocketService | null = null;
  private isInitialized = false;
  private appId: string;
  private isEnabled: boolean;

  // Centralized sensitive URL patterns for privacy
  private SENSITIVE_PATTERNS: string[] = [
    '/api/auth',
    '/auth',
    '/login',
    '/password',
    '/token',
    '/credentials',
    '/reset-password',
    '/forgot-password',
  ];

  // Configuration options
  private config = {
    maxRetries: 5,
    retryDelay: 2000, // ms
    initialCheckDelay: 1000, // ms
    silentMode: false, // Suppress console logs
  };

  // Track timers for cleanup
  private activeTimers: NodeJS.Timeout[] = [];

  constructor() {
    // Get LogRocket configuration from config
    this.appId = config.logRocket.appId;
    this.isEnabled = !!this.appId; // Enable if appId has a valid value
  }

  /**
   * Update service configuration
   * @param options - Configuration options to update
   */
  configure(options: Partial<typeof this.config>): void {
    this.config = { ...this.config, ...options };
  }

  /**
   * Add custom sensitive URL patterns
   * @param patterns - Array of URL patterns to filter
   */
  addSensitivePatterns(patterns: string[]): void {
    this.SENSITIVE_PATTERNS.push(...patterns);
  }

  /**
   * Get current sensitive patterns
   * @returns Array of sensitive URL patterns
   */
  getSensitivePatterns(): readonly string[] {
    return [...this.SENSITIVE_PATTERNS];
  }

  /**
   * Singleton getInstance method
   * Ensures only one LogRocket instance exists throughout the application
   */
  static getInstance(): LogRocketService {
    if (!LogRocketService.instance) {
      LogRocketService.instance = new LogRocketService();
    }
    return LogRocketService.instance;
  }

  /**
   * Check if LogRocket should be initialized
   * Returns false if no app ID or disabled
   */
  get shouldInitialize(): boolean {
    return this.isEnabled && !!this.appId;
  }

  /**
   * Initialize LogRocket with full configuration
   * Safe to call multiple times - will only initialize once
   */
  initialize(): void {
    // Don't initialize if LogRocket is disabled or missing app ID
    if (this.isInitialized || !this.shouldInitialize) {
      this.log('log', 'LogRocket initialization skipped:', {
        isInitialized: this.isInitialized,
        shouldInitialize: this.shouldInitialize,
        appId: this.appId,
        isEnabled: this.isEnabled,
      });
      return;
    }

    try {
      // Initialize LogRocket with full screen capture configuration
      LogRocket.init(this.appId, {
        // Domain configuration (add your domains)
        dom: {
          isEnabled: true,
        },

        console: {
          isEnabled: true,
          shouldAggregateConsoleErrors: true,
        },

        network: {
          isEnabled: true,
          requestSanitizer: request => {
            // Don't record sensitive API calls
            if (
              this.SENSITIVE_PATTERNS.some(pattern =>
                request.url.toLowerCase().includes(pattern.toLowerCase())
              )
            ) {
              return null;
            }

            // Remove sensitive headers
            if (request.headers) {
              const headers = { ...request.headers };
              if (headers.Authorization) {
                headers.Authorization = '[REDACTED]';
              }
              if (headers.Cookie) {
                headers.Cookie = '[REDACTED]';
              }
              request.headers = headers;
            }

            return request;
          },
          responseSanitizer: response => {
            // Don't record sensitive responses
            if (
              response.url &&
              this.SENSITIVE_PATTERNS.some(pattern =>
                response.url!.toLowerCase().includes(pattern.toLowerCase())
              )
            ) {
              return null;
            }

            return response;
          },
        },
      });

      this.isInitialized = true;
      this.log('log', '✅ LogRocket initialized successfully with app ID:', this.appId);

      // Check if recording is working
      this.checkRecordingStatus();
    } catch (error) {
      this.log('error', '❌ Failed to initialize LogRocket:', error);
    }
  }

  /**
   * Log message (respects silent mode)
   */
  private log(level: 'log' | 'warn' | 'error', ...args: any[]): void {
    if (!this.config.silentMode) {
      console[level](...args);
    }
  }

  /**
   * Check LogRocket recording status with multiple attempts
   * Tries up to maxRetries times to confirm recording has started
   */
  private checkRecordingStatus(): void {
    const checkStatus = (attempt: number) => {
      try {
        const sessionURL = LogRocket.sessionURL;
        const isRecording = !!sessionURL;

        this.log(
          'log',
          `🔍 LogRocket Status Check (Attempt ${attempt}/${this.config.maxRetries}):`,
          {
            sessionURL,
            isRecording,
            timestamp: new Date().toISOString(),
          }
        );

        if (sessionURL) {
          this.log('log', '✅ LogRocket session URL:', sessionURL);
          this.log('log', '✅ LogRocket is recording successfully');
          return;
        }

        if (attempt < this.config.maxRetries) {
          // Try again after configured delay
          const timer = setTimeout(() => checkStatus(attempt + 1), this.config.retryDelay);
          this.activeTimers.push(timer);
        } else {
          this.log(
            'warn',
            `⚠️ LogRocket session URL still not available after ${this.config.maxRetries} attempts`
          );

          // Try to manually trigger recording
          try {
            LogRocket.track('manual_recording_test', {
              timestamp: new Date().toISOString(),
              attempt: attempt,
            });
            this.log('log', '📊 Sent test event to LogRocket');
          } catch (trackError) {
            this.log('error', '❌ Failed to send test event:', trackError);
          }
        }
      } catch (error) {
        this.log('error', `Error checking LogRocket session (attempt ${attempt}):`, error);
      }
    };

    // Start checking after configured delay
    const initialTimer = setTimeout(() => checkStatus(1), this.config.initialCheckDelay);
    this.activeTimers.push(initialTimer);
  }

  /**
   * User information structure for identification
   */
  private extractUserData(userInfo: any): {
    userId: string;
    userName: string;
    userEmail: string;
    department: string;
    role: string;
    module: string;
  } {
    return {
      userId: userInfo?.userInfo?.id || userInfo?.id || 'unknown',
      userName: userInfo?.userInfo?.name || userInfo?.name || '',
      userEmail: userInfo?.userInfo?.email || userInfo?.email || '',
      department: userInfo?.userInfo?.deptId?.departmentName || userInfo?.department || '',
      role: userInfo?.roles?.pps?.[0]?.roleId?.roleName || userInfo?.role || '',
      module: userInfo?.roles?.pps?.[0]?.moduleId?.moduleName || userInfo?.module || '',
    };
  }

  /**
   * Identify user for session tracking
   * Call this after user authentication to link sessions to users
   *
   * @param userInfo - User information object
   */
  identifyUser(userInfo: any): void {
    if (!this.isInitialized || !this.shouldInitialize) {
      return;
    }

    try {
      // Extract user details using helper method
      const userData = this.extractUserData(userInfo);

      LogRocket.identify(userData.userId, {
        name: userData.userName,
        email: userData.userEmail,
        department: userData.department,
        role: userData.role,
        module: userData.module,
        loginTime: new Date().toISOString(),
        userAgent: navigator.userAgent,
        // Add any other user metadata you want to track
        userModulesRoles: userInfo?.userModulesRoles,
      });

      this.log('log', '✅ User identified in LogRocket:', userData.userEmail || userData.userId);
    } catch (error) {
      this.log('error', '❌ Failed to identify user in LogRocket:', error);
    }
  }

  /**
   * Track custom events
   * Use this to track important user actions or application events
   *
   * @param eventName - Name of the event
   * @param properties - Event properties
   */
  trackEvent(eventName: string, properties?: any): void {
    if (!this.isInitialized || !this.shouldInitialize) {
      return;
    }

    try {
      LogRocket.track(eventName, properties);
      this.log('log', '📊 Event tracked:', eventName, properties);
    } catch (error) {
      this.log('error', '❌ Failed to track event in LogRocket:', error);
    }
  }

  /**
   * Capture custom errors
   * Use this to log errors with additional context
   *
   * @param error - Error object
   * @param context - Additional context (component, action, etc.)
   */
  captureError(error: Error, context?: any): void {
    if (!this.isInitialized || !this.shouldInitialize) {
      return;
    }

    try {
      LogRocket.captureException(error, {
        tags: {
          component: context?.component || 'Unknown',
          action: context?.action || 'Unknown',
          errorType: context?.errorType || 'GENERAL_ERROR',
        },
        extra: context,
      });
      this.log('log', '❌ Error captured in LogRocket:', error.message);
    } catch (logRocketError) {
      this.log('error', '❌ Failed to capture error in LogRocket:', logRocketError);
    }
  }

  /**
   * Get current session URL for debugging or support
   * Use this to get a link to the current session recording
   *
   * @returns Session URL or null if not available
   */
  getSessionURL(): string | null {
    if (!this.isInitialized || !this.shouldInitialize) {
      return null;
    }

    try {
      return LogRocket.sessionURL;
    } catch (error) {
      this.log('error', '❌ Failed to get LogRocket session URL:', error);
      return null;
    }
  }

  /**
   * Clean up LogRocket
   * Called when app is unmounting or cleaning up
   */
  cleanup(): void {
    if (this.isInitialized) {
      try {
        // Clear all active timers
        this.activeTimers.forEach(timer => clearTimeout(timer));
        this.activeTimers = [];

        // LogRocket doesn't have a shutdown method, just reset our state
        this.isInitialized = false;
        this.log('log', '🧹 LogRocket cleaned up');
      } catch (error) {
        this.log('error', '❌ Failed to cleanup LogRocket:', error);
      }
    }
  }

  /**
   * Check if LogRocket is currently active
   * @returns true if LogRocket is initialized and enabled
   */
  get isActive(): boolean {
    return this.isInitialized && this.shouldInitialize;
  }

  /**
   * Get detailed status of LogRocket
   * Useful for debugging and monitoring
   *
   * @returns Status object with initialization details
   */
  getStatus(): any {
    try {
      return {
        isInitialized: this.isInitialized,
        isEnabled: this.isEnabled,
        appId: this.appId,
        shouldInitialize: this.shouldInitialize,
        sessionURL: LogRocket.sessionURL || null,
        isRecording: !!LogRocket.sessionURL, // Check recording status by sessionURL presence
        logRocketObject:
          typeof LogRocket !== 'undefined' ? 'LogRocket loaded' : 'LogRocket not loaded',
        currentDomain: window.location.hostname,
        userAgent: navigator.userAgent,
      };
    } catch (error) {
      return {
        error: 'Failed to get LogRocket status',
        details: error,
      };
    }
  }

  /**
   * Test LogRocket functionality manually
   * Useful for debugging - sends test events and errors
   */
  testLogRocket(): void {
    if (!this.isInitialized) {
      this.log('error', '❌ LogRocket not initialized');
      return;
    }

    try {
      // Test tracking
      LogRocket.track('logrocket_test', {
        timestamp: new Date().toISOString(),
        domain: window.location.hostname,
        userAgent: navigator.userAgent,
      });
      this.log('log', '✅ Test event sent to LogRocket');

      // Test console logging
      this.log('log', '🧪 This is a test log message for LogRocket');

      // Test error capture
      const testTimer = setTimeout(() => {
        try {
          throw new Error('Test error for LogRocket');
        } catch (error) {
          LogRocket.captureException(error as Error);
          this.log('log', '✅ Test error sent to LogRocket');
        }
      }, 1000);
      this.activeTimers.push(testTimer);
    } catch (error) {
      this.log('error', '❌ Failed to test LogRocket:', error);
    }
  }
}

export default LogRocketService;
